import React from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangle, Info } from "lucide-react";
import { Card, CritPill, Stat } from "@/components/controls/ui";
import { lbl } from "@/lib/controls";

const scoreTone = (s) => (s >= 90 ? "text-emerald-600" : s >= 70 ? "text-amber-600" : "text-red-600");
const Bars = ({ obj, testId }) => {
  const max = Math.max(1, ...Object.values(obj || {}));
  return (
    <div className="space-y-1.5" data-testid={testId}>
      {Object.entries(obj || {}).map(([k, v]) => (
        <div key={k} className="flex items-center gap-2 text-xs">
          <div className="w-40 truncate text-slate-600">{lbl(k)}</div>
          <div className="flex-1 bg-slate-100 rounded h-2"><div className="bg-[#008080] h-2 rounded" style={{ width: `${(v / max) * 100}%` }} /></div>
          <div className="w-6 text-right font-mono text-slate-700">{v}</div>
        </div>
      ))}
    </div>
  );
};

export const ControlDashboard = ({ d }) => {
  const nav = useNavigate();
  if (!d) return <div className="text-sm text-slate-500">Loading...</div>;
  const t = d.totals;
  return (
    <div className="space-y-5" data-testid="control-dashboard">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 bg-[#003366] text-white rounded-xl p-5" data-testid="operational-control-health">
          <div className="text-[11px] uppercase tracking-widest text-blue-200">Operational Control Health</div>
          <div className="text-5xl font-bold mt-2" data-testid="operational-control-health-value">{d.operational_control_health ?? "-"}<span className="text-2xl">%</span></div>
          <div className="text-xs text-blue-100 mt-1">{d.eligible_controls} active, applicable controls</div>
          <div className="mt-4 text-[11px] text-blue-200 flex gap-1.5"><Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />{d.disclaimer}</div>
        </div>
        <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total controls" value={t.total} testId="stat-total" hint={`${t.active} active · ${t.planned} planned`} />
          <Stat label="Due this week" value={t.due_this_week} testId="stat-due-week" />
          <Stat label="Overdue executions" value={t.overdue_executions} tone={t.overdue_executions ? "text-red-600" : undefined} testId="stat-overdue" />
          <Stat label="Tests due (30d)" value={t.tests_due} testId="stat-tests-due" />
          <Stat label="Open deficiencies" value={t.open_deficiencies} testId="stat-open-defs" hint={`${t.high_critical_deficiencies} high / critical`} />
          <Stat label="Evidence missing" value={t.evidence_missing} tone={t.evidence_missing ? "text-amber-600" : undefined} testId="stat-evidence-missing" />
          <Stat label="Evidence expired" value={t.evidence_expired} testId="stat-evidence-expired" />
          <Stat label="Scheduler warnings" value={t.warnings} testId="stat-warnings" />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Controls by domain"><Bars obj={d.by_domain} testId="chart-by-domain" /></Card>
        <Card title="Controls by effectiveness"><Bars obj={d.by_effectiveness} testId="chart-by-effectiveness" /></Card>
        <Card title="Controls by owner"><Bars obj={d.by_owner} testId="chart-by-owner" /></Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Control execution trend (6 months)" testId="chart-exec-trend">
          <ResponsiveContainer width="100%" height={200}><BarChart data={d.execution_trend}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
            <XAxis dataKey="month" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="completed" fill="#008080" name="Completed" /><Bar dataKey="exceptions" fill="#f59e0b" name="With exception" /><Bar dataKey="overdue" fill="#dc2626" name="Overdue" />
          </BarChart></ResponsiveContainer>
        </Card>
        <Card title="Test-result trend (6 months)" testId="chart-test-trend">
          <ResponsiveContainer width="100%" height={200}><BarChart data={d.test_trend}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
            <XAxis dataKey="month" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="EFFECTIVE" stackId="a" fill="#059669" name="Effective" /><Bar dataKey="NEEDS_IMPROVEMENT" stackId="a" fill="#fbbf24" name="Needs improvement" />
            <Bar dataKey="INEFFECTIVE" stackId="a" fill="#dc2626" name="Ineffective" />
          </BarChart></ResponsiveContainer>
        </Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Deficiency ageing (open, days)"><Bars obj={d.deficiency_ageing} testId="chart-def-ageing" /></Card>
        <Card title="Remediation ageing (linked, open)"><Bars obj={d.remediation_ageing} testId="chart-rem-ageing" /></Card>
        <Card title="Lowest control health" testId="health-worst">
          <div className="space-y-1.5">
            {d.health_by_control.slice(0, 6).map((r) => (
              <button key={r.id} onClick={() => nav(`/controls/${r.id}`)} className="w-full flex items-center gap-2 text-left text-xs hover:bg-slate-50 rounded px-1 py-1" data-testid={`health-row-${r.control_code}`}>
                <span className={`w-10 font-mono font-bold ${scoreTone(r.score)}`}>{r.score}</span>
                <span className="flex-1 truncate"><span className="font-mono text-slate-500">{r.control_code}</span> {r.control_name}<span className="block text-[10px] text-slate-400">{r.reason}</span></span>
                <CritPill s={r.criticality} />
              </button>
            ))}
            {!d.health_by_control.length && <div className="text-xs text-slate-500 flex gap-1"><AlertTriangle className="w-3.5 h-3.5" />No active controls yet.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
};
